/**
 * Format equipment availability for badges and pickers.
 *
 * @param {object|null|undefined} availability
 * @returns {{ label: string, color: string }}
 */
export function getAvailabilityBadge(availability) {
  if (!availability) {
    return { label: "Unknown", color: "gray" };
  }

  if (availability.state === "available") {
    return { label: "Available", color: "green" };
  }

  const reason = availability.reasonLabel || availability.reason || "Unavailable";
  if (availability.reason === "maintenance" || availability.reason === "work_order") {
    return { label: reason, color: "yellow" };
  }

  return { label: reason, color: "red" };
}

/**
 * @param {object|null|undefined} availability
 * @returns {boolean}
 */
export function isEquipmentAvailable(availability) {
  if (!availability || availability.state == null) {
    return true;
  }
  return availability.state === "available";
}

/**
 * Options for leg/dispatch equipment pickers — available units only,
 * plus the currently assigned unit when editing an existing leg.
 *
 * @param {object[]} [items]
 * @param {string|null|undefined} selectedId
 * @returns {{ value: string, label: string }[]}
 */
export function buildFleetEquipmentSelectOptions(
  items = [],
  selectedId = null,
  { availableOnly = true } = {},
) {
  const selected = selectedId ? String(selectedId) : null;

  return (items || [])
    .filter((item) => {
      if (!item?._id) return false;
      if (!availableOnly) return true;
      const id = String(item._id);
      if (selected && id === selected) return true;
      return isEquipmentAvailable(item.availability);
    })
    .map((item) => {
      const id = String(item._id);
      const name = item.name || item.licensePlate || "Unit";
      const isCurrent = selected && id === selected;
      const unavailable = !isEquipmentAvailable(item.availability);

      if (isCurrent && unavailable) {
        const reason =
          item.availability?.reasonLabel ||
          getAvailabilityBadge(item.availability).label;
        return { value: item._id, label: `${name} (${reason} — current)` };
      }

      return { value: item._id, label: name };
    });
}
